import { Admin, Resource, defaultTheme } from "react-admin";
import { createMuiTheme } from "@material-ui/core/styles";
import "./App.css";
import Menu from "./components/Menu";
import { TokenContextProvider, useTokenOps } from "./components/TokenContext";
import authProvider from "./providers/authProvider";
import useDataProvider from "./providers/dataProvider";
import accountsResource from "./resources/AccountResource";
import articlesResource from "./resources/ArticleResource";
import articleDppResource from "./resources/ArticleDppResource";
import projectsResource from "./resources/ProjectResource";
import projectPackagingResources from "./resources/ProjectPackagingResource";
import circularityDashboardResource from "./resources/CircularityDashboardResource";
import usersResource from "./resources/UserResource";
import commercialResource from "./resources/CommercialResource";
import useAuthorization from "./hooks/useAuthorization";
import { selectGlobalResources } from "./auth/globalResourceAccess";
import PublicClientIntakePage from "./commercial/PublicClientIntakePage";


import AppLayout from "./components/AppLayout";
import accountBestPracticesResource from "./resources/AccountBestPracticesResource";
import accountFeaturesResource from "./resources/accountFeaturesResource";
import ecobalyseResource from "./resources/EcobalyseResource";
import accountArticleGtinResource from "./resources/AccountArticleGtinResource";
import articleCategoryResource from "./resources/ArticleCategoryResource";
import articleGroupResource from "./resources/ArticleGroupResource";
import articleHazardousResource from "./resources/ArticleHazardousResource";
import articleMaterialResource from "./resources/ArticleMaterialResource";
import articlePackagingResource from "./resources/ArticlePackagingResource";
import articleProcessResource, { articleFinishingResource, articleManufacturingResource, articleTrimmingResource } from "./resources/ArticleProcessResource";
import {
  articleImpactCircularityResource,
  articleImpactEcobalyseResource,
  articleImpactEcoscoreResource,
  articleImpactLcaResource,
} from "./resources/ArticleImpactResource";
import articleSupplierResource from "./resources/ArticleSupplierResource";
import {
  articleProjectResource,
  articleApplicationResource,
  materialProjectResource,
  supplierProjectResource,
  retailPackagingProjectResource,
} from "./resources/ProjectTablesResource";
import bestPracticesResource from "./resources/BestPracticesResource";
import certificatesResource from "./resources/CertificatesResource";
import defaultArticleFinishingResource from "./resources/DefaultArticleFinishingResource";
import defaultArticleTrimmingResource from "./resources/DefaultArticleTrimmingResource";
import defaultArticleManufacturingResource from "./resources/DefaultArticleManufacturingResource";
import defaultArticlePackagingResource from "./resources/DefaultArticlePackagingResource";
import defaultMaterialFinishingResource from "./resources/DefaultMaterialFinishingResource";
import defaultMaterialProcessingResource from "./resources/DefaultMaterialProcessingResource";
import digitalizationDataAccountResource from "./resources/DigitalizationDataAccountResource";
import digitalizationDataResource from "./resources/DigitalizationDataResource";
import entityResource from "./resources/EntityResource";
import equivalencesResource from "./resources/EquivalencesResource";
import evaluationCategoryResource from "./resources/EvaluationCategoryResource";
import evaluationGroupResource from "./resources/EvaluationGroupResource";
import featuresResource from "./resources/FeaturesResource";
import hazardousSusbtancesResource from "./resources/HazardousSusbtancesResource";
import lcaCategoryResource from "./resources/LcaCategoryResource";
import lcaGroupResource from "./resources/LcaGroupResource";
import libraryResource from "./resources/LibraryResource";
import materialCertificateResource from "./resources/MaterialCertificateResource";
import {
  materialExtractionResource,
  materialFinishingResource,
  materialProcessingResource
} from "./resources/MaterialProcessResource";
import materialImpactResource from "./resources/MaterialImpactResource";
import materialResource from "./resources/MaterialResource";
import materialsApplicationResource from "./resources/MaterialsApplicationResource";
import materialsCategoryResource from "./resources/MaterialsCategoryResource";
import materialsGroupResource from "./resources/MaterialsGroupResource";
import originsResource from "./resources/OriginsResource";
import packagingCategoryResource from "./resources/PackagingCategoryResource";
import packagingGroupResource from "./resources/PackagingGroupResource";
import processMaterialsCategoryResource from "./resources/ProcessMaterialsCategoryResource";
import {
  extractionOriginResource,
  finishingOriginResource,
  manufacturingOriginResource,
  packagingOriginResource,
  processingOriginResource,
  transportOriginResource as processOriginTransportResource
} from "./resources/ProcessOriginResource";
import {
  accountExtractionOriginResource,
  accountFinishingOriginResource,
  accountManufacturingOriginResource,
  accountPackagingOriginResource,
  accountProcessingOriginResource,
  accountTransportOriginResource,
  accountTrimmingOriginResource,
} from "./resources/AccountProcessOriginResource";
import {
  extractionResource,
  finishingResource,
  manufacturingResource,
  packagingResource,
  processingResource,
  transportResource as processTransportResource,
} from "./resources/ProcessResource";
import { trimmingResource } from "./resources/TrimmingResource";
import { trimmingOriginResource } from "./resources/TrimmingOriginResource";
import rawMaterialCategoryMaterialCategoryResource from "./resources/RawMaterialCategoryMaterialCategory";
import rawMaterialCategoryMaterialGroupResource from "./resources/RawMaterialCategoryMaterialGroupResource";
import rawMaterialCategoryResource from "./resources/RawMaterialsCategoryResource";
import rawMaterialGroupResource from "./resources/RawMaterialsGroupResource";
import retailGroupResource from "./resources/RetailGroupResource";
import retailPackagingProcessesResource from "./resources/RetailPackagingProcessesResource";
import retailPackagingResource from "./resources/RetailPackagingResource";
import supplierCertificateResource from "./resources/SupplierCertificateResource";
import supplierResource from "./resources/SupplierResource";
import transportOriginResource from "./resources/TransportOriginResource";
import uploadStatusResource from "./resources/uploadStatusResource";
import calculationLogsResource from "./resources/CalculationLogsResource";
import auditEventResource from "./resources/AuditEventResource";
import viewsResource from "./resources/ViewResource";
import databaseUploadResource from "./resources/DatabaseUploadResource";
import databaseVersionResource from "./resources/DatabaseVersionResource";
import lcaMethodologyVersionResource from "./resources/LcaMethodologyVersionResource";
import circularityVersionResource from "./resources/CircularityVersionResource";
import circularityDefaultsResource from "./resources/CircularityDefaultsResource";
import circularityArticleMetricsResource from "./resources/CircularityArticleMetricsResource";
import bcomeVersionResource from "./resources/BcomeVersionResource";
import iso3758CareCodesResource from "./resources/Iso3758CareCodesResource";
import carbonFactorReleaseResource from "./resources/CarbonFactorReleaseResource";
import carbonReferenceDataResource from "./resources/CarbonReferenceDataResource";
import {
  carbonCombustionFactorResource,
  carbonElectricityFactorResource,
  carbonRefrigerantFactorResource,
} from "./resources/CurrentCarbonFactorResource";
import carbonProfileResource from "./resources/CarbonProfileResource";
import carbonInventoryResource from "./resources/CarbonInventoryResource";
import carbonSiteResource from "./resources/CarbonSiteResource";
import carbonBoundaryResource from "./resources/CarbonBoundaryResource";
import carbonActivityResource from "./resources/CarbonActivityResource";
import carbonAuditResource from "./resources/CarbonAuditResource";
import carbonEvidenceResource from "./resources/CarbonEvidenceResource";
import carbonCalculationResource from "./resources/CarbonCalculationResource";
import carbonResultResource from "./resources/CarbonResultResource";
import carbonExportResource from "./resources/CarbonExportResource";
import carbonFinalizationResource from "./resources/CarbonFinalizationResource";
import accountCarbonUploadResource from "./resources/account/AccountCarbonUploadResource";
import businessRulesCatalogResource from "./resources/BusinessRulesCatalogResource";
import processApplicabilityResource from "./resources/ProcessApplicabilityResource";
import weightSharesResource from "./resources/WeightSharesResource";
import fieldDefinitionsCoverageResource from "./resources/FieldDefinitionsCoverageResource";
import accountTranslationsResource from "./resources/AccountTranslationsResource";


const theme = createMuiTheme({
  ...defaultTheme,
  overrides: {
    ...(defaultTheme.overrides || {}),
    RaTopToolbar: {
      root: {
        justifyContent: 'flex-start',
      },
    },
    RaListToolbar: {
      toolbar: {
        position: 'sticky',
        left: 0,
        width: 'fit-content',
      },
    },
    RaList: {
      // El content del List es un Card de MUI, que trae overflow hidden, y el
      // layout tambien recorta (ver AppLayout). Sin esto las tablas mas anchas
      // que el area de contenido quedan cortadas sin barra de scroll.
      content: {
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'auto',
      },
    },
  },
});

const AdminApp = () => {
  const tokenOps = useTokenOps();
  const dataProvider = useDataProvider(tokenOps);
  const authorization = useAuthorization();
  const globalResources = selectGlobalResources(authorization, {
    accounts: accountsResource,
    commercial: commercialResource,
    circularityDefaults: circularityDefaultsResource,
    auditEvents: auditEventResource,
  }, {
    isSwitchAccount: tokenOps.isSwitchAccount,
  });
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider(tokenOps)}
      menu={Menu}
      appLayout={AppLayout}
      theme={theme}
    >
      {/* Accounts */}
      {globalResources}
      {accountCarbonUploadResource()}
      {accountBestPracticesResource()}
      {accountFeaturesResource()}
      {digitalizationDataAccountResource()}
      {ecobalyseResource()}
      {accountArticleGtinResource()}
      {featuresResource()}
      {libraryResource()}
      {usersResource()}
      {accountTranslationsResource()}

      {/* Projects */}
      {projectsResource()}
      {circularityDashboardResource()}
      {viewsResource()}

      {/* Articles */}
      {articlesResource()}
      {articleDppResource()}
      {articleImpactLcaResource()}
      {articleImpactEcoscoreResource()}
      {articleImpactCircularityResource()}
      {circularityArticleMetricsResource()}
      {articleImpactEcobalyseResource()}
      {articleProjectResource()}
      {articleApplicationResource()}
      {articleCategoryResource()}
      {articleFinishingResource()}
      {articleGroupResource()}
      {articleHazardousResource()}
      {articleManufacturingResource()}
      {articleMaterialResource()}
      {articlePackagingResource()}
      {articleProcessResource()}
      {articleTrimmingResource()}
      {articleSupplierResource()}
      {digitalizationDataResource()}
      {entityResource()}

      {/* Materials */}
      {materialResource()}
      {materialProjectResource()}
      {materialCertificateResource()}
      {materialExtractionResource()}
      {materialFinishingResource()}
      {materialProcessingResource()}
      {materialImpactResource()}

      {/* Suppliers */}
      {supplierResource()}
      {supplierProjectResource()}
      {supplierCertificateResource()}

      {/* Packaging */}
      {/* {packagingResource()}
      {packagingProcessResource()} */}
      {retailPackagingResource()}
      {retailPackagingProjectResource()}
      {projectPackagingResources()}

      {bestPracticesResource()}
      {certificatesResource()}
      {/* Bcome */}
      {businessRulesCatalogResource()}
      {processApplicabilityResource()}
      {weightSharesResource()}
      {fieldDefinitionsCoverageResource()}
      {databaseUploadResource()}
      {databaseVersionResource()}
      {lcaMethodologyVersionResource()}
      {circularityVersionResource()}
      {bcomeVersionResource()}
      {iso3758CareCodesResource()}
      {carbonCombustionFactorResource()}
      {carbonRefrigerantFactorResource()}
      {carbonElectricityFactorResource()}
      {carbonFactorReleaseResource()}
      {carbonReferenceDataResource()}
      {carbonProfileResource()}
      {carbonInventoryResource()}
      {carbonSiteResource()}
      {carbonBoundaryResource()}
      {carbonActivityResource()}
      {carbonAuditResource()}
      {carbonEvidenceResource()}
      {carbonCalculationResource()}
      {carbonResultResource()}
      {carbonExportResource()}
      {carbonFinalizationResource()}
      {defaultArticleFinishingResource()}
      {defaultArticleManufacturingResource()}
      {defaultArticleTrimmingResource()}
      {defaultArticlePackagingResource()}
      {defaultMaterialFinishingResource()}
      {defaultMaterialProcessingResource()}
      {equivalencesResource()}
      {evaluationCategoryResource()}
      {evaluationGroupResource()}
      {extractionOriginResource()}
      {extractionResource()}
      {finishingOriginResource()}
      {finishingResource()}
      {hazardousSusbtancesResource()}
      {lcaCategoryResource()}
      {lcaGroupResource()}
      {manufacturingOriginResource()}
      {manufacturingResource()}
      {materialsApplicationResource()}
      {materialsCategoryResource()}
      {materialsGroupResource()}
      {originsResource()}
      {packagingCategoryResource()}
      {packagingGroupResource()}
      {packagingOriginResource()}
      {packagingResource()}
      {processMaterialsCategoryResource()}
      {processOriginTransportResource()}
      {processingOriginResource()}
      {/* Process by account */}
      {accountExtractionOriginResource()}
      {accountFinishingOriginResource()}
      {accountManufacturingOriginResource()}
      {accountPackagingOriginResource()}
      {accountProcessingOriginResource()}
      {accountTransportOriginResource()}
      {accountTrimmingOriginResource()}
      {processingResource()}
      {processTransportResource()}
      {trimmingResource()}
      {trimmingOriginResource()}
      {rawMaterialCategoryMaterialCategoryResource()}
      {rawMaterialCategoryMaterialGroupResource()}
      {rawMaterialCategoryResource()}
      {rawMaterialGroupResource()}
      {retailGroupResource()}
      {retailPackagingProcessesResource()}
      {transportOriginResource()}
      <Resource name="process" />
      <Resource name="process/extraction" />
      <Resource name="process/finishing" />
      <Resource name="process/manufacturing" />
      <Resource name="process/packaging" />
      <Resource name="process/processing" />
      <Resource name="process/transport" />
      <Resource name="process/trimmings" />
      <Resource name="transportModes" />
      <Resource name="database-context" />

      {/* Upload status */}
      {uploadStatusResource()}
      {calculationLogsResource()}

    </Admin>
  );
}

const App = () => {
  const intakeMatch = window.location.hash.match(
    /^#\/client-intake\/([a-f0-9]{64})$/i,
  );
  if (intakeMatch) {
    return <PublicClientIntakePage token={intakeMatch[1]} />;
  }
  return (
    <TokenContextProvider>
      <AdminApp />
    </TokenContextProvider>
  );
};

export default App;
